import React, { useState, useEffect, useCallback, useRef } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, Building2, Wallet, Loader2, CheckCircle, Search,
  ChevronRight, ChevronDown, AlertTriangle, ArrowRight, User,
  Plus, Trash2, BookmarkCheck, X, XCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `NGN ${Math.round(Number(n || 0)).toLocaleString()}`;
const generateIdemKey = () => `PBL-IDEM-SA-${Date.now()}-${Math.random().toString(36).slice(2,10).toUpperCase()}`;

export default function SuperAdminTransferPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('wallet'); // wallet, form, confirm, processing, success, failed
  const [wallets, setWallets] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Wallet selection
  const [walletSearch, setWalletSearch] = useState('');
  const [walletFilter, setWalletFilter] = useState('');
  const [selectedWallet, setSelectedWallet] = useState(null);

  // Transfer form
  const [form, setForm] = useState({
    bank_name: '', bank_code: '', account_number: '',
    account_name: '', name_enq_ref: '', amount: '', narration: '',
  });
  const [bankSearch, setBankSearch] = useState('');
  const [showBankList, setShowBankList] = useState(false);
  const [resolving, setResolving] = useState(false);

  // OTP
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const idemKeyRef = useRef(generateIdemKey());

  // Beneficiaries
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);
  const [addingBeneficiary, setAddingBeneficiary] = useState(false);
  const [newBeneficiary, setNewBeneficiary] = useState({ bank_name: '', bank_code: '', account_number: '', account_name: '', label: '' });
  const [newBenResolving, setNewBenResolving] = useState(false);
  const [newBenBankSearch, setNewBenBankSearch] = useState('');
  const [showNewBenBankList, setShowNewBenBankList] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [w, b, ben] = await Promise.all([
          superAdminAPI.getTransferWallets({ search: '', wallet_type: '' }),
          superAdminAPI.getTransferBanks(),
          superAdminAPI.getBeneficiaries(),
        ]);
        setWallets(w || []);
        setBanks(b || []);
        setBeneficiaries(ben || []);
      } catch (e) { toast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const searchWallets = useCallback(async (search, type) => {
    try {
      const w = await superAdminAPI.getTransferWallets({ search, wallet_type: type });
      setWallets(w || []);
    } catch {}
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchWallets(walletSearch, walletFilter), 400);
    return () => clearTimeout(timer);
  }, [walletSearch, walletFilter, searchWallets]);

  const filteredBanks = banks.filter(b =>
    (b.name || b.bankName || '').toLowerCase().includes(bankSearch.toLowerCase())
  );

  const selectBank = (bank) => {
    setForm(f => ({ ...f, bank_name: bank.name || bank.bankName, bank_code: bank.code || bank.bankCode, account_name: '', name_enq_ref: '' }));
    setShowBankList(false);
    setBankSearch('');
  };

  const resolveAccount = useCallback(async () => {
    if (form.account_number.length < 10 || !form.bank_code) return;
    setResolving(true);
    try {
      const res = await superAdminAPI.resolveTransferAccount({
        bank_code: form.bank_code, account_number: form.account_number,
      });
      setForm(f => ({ ...f, account_name: res.account_name, name_enq_ref: res.name_enq_ref || '' }));
    } catch { toast.error('Could not resolve account'); }
    finally { setResolving(false); }
  }, [form.account_number, form.bank_code]);

  useEffect(() => {
    if (form.account_number.length === 10 && form.bank_code) resolveAccount();
  }, [form.account_number, form.bank_code, resolveAccount]);

  const fee = 10;
  const amount = parseFloat(form.amount) || 0;
  const total = amount + fee;

  const handleRequestOtp = async () => {
    setSendingOtp(true);
    try {
      await superAdminAPI.sendTransferOtp();
      setOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch { toast.error('Failed to send OTP'); }
    finally { setSendingOtp(false); }
  };

  const handleSend = async () => {
    if (!otp || otp.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setSending(true);
    try {
      const res = await superAdminAPI.executeTransfer({
        wallet_id: selectedWallet.id,
        bank_name: form.bank_name, bank_code: form.bank_code,
        account_number: form.account_number, account_name: form.account_name,
        name_enq_ref: form.name_enq_ref,
        amount, narration: form.narration, otp,
      }, idemKeyRef.current);
      setResult(res);
      if (res.status === 'processing') {
        setStep('processing');
        setPollCount(0);
      } else if (res.status === 'completed') {
        setStep('success');
      } else {
        setStep('processing');
        setPollCount(0);
      }
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error('This transfer is already being processed. Please wait.');
      } else if (e.response?.status === 502) {
        toast.error(e.response?.data?.detail || 'Banking service is currently unreachable. Your funds have been returned.');
      } else {
        toast.error(e.response?.data?.detail || 'Transfer failed');
      }
    }
    finally { setSending(false); }
  };

  // Poll transfer status when in processing state
  useEffect(() => {
    if (step !== 'processing' || !result?.reference) return;
    if (pollCount >= 12) return; // Stop after 60 seconds
    
    const timer = setTimeout(async () => {
      try {
        const statusRes = await superAdminAPI.getTransferStatus(result.reference);
        if (statusRes.status === 'completed') {
          setResult(prev => ({ ...prev, status: 'completed', new_balance: statusRes.new_balance }));
          setStep('success');
          return;
        }
        if (statusRes.status === 'failed') {
          setResult(prev => ({ ...prev, status: 'failed', new_balance: statusRes.new_balance, failure_reason: statusRes.failure_reason }));
          setStep('failed');
          return;
        }
        setPollCount(c => c + 1);
      } catch {
        setPollCount(c => c + 1);
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [step, pollCount, result?.reference]);

  const resetForm = () => {
    setStep('wallet');
    setSelectedWallet(null);
    setForm({ bank_name: '', bank_code: '', account_number: '', account_name: '', name_enq_ref: '', amount: '', narration: '' });
    setOtp(''); setOtpSent(false); setResult(null); setPollCount(0);
    idemKeyRef.current = generateIdemKey();
  };

  const handleUseBeneficiary = (ben) => {
    setForm(f => ({
      ...f,
      bank_name: ben.bank_name,
      bank_code: ben.bank_code,
      account_number: ben.account_number,
      account_name: ben.account_name,
      name_enq_ref: '',
    }));
  };

  const handleAddBeneficiary = async () => {
    if (!newBeneficiary.account_name || !newBeneficiary.bank_code || !newBeneficiary.account_number) {
      toast.error('Resolve the account first'); return;
    }
    setAddingBeneficiary(true);
    try {
      const saved = await superAdminAPI.addBeneficiary({
        account_name: newBeneficiary.account_name,
        bank_name: newBeneficiary.bank_name,
        bank_code: newBeneficiary.bank_code,
        account_number: newBeneficiary.account_number,
        label: newBeneficiary.label || null,
      });
      setBeneficiaries(prev => [saved, ...prev]);
      setShowAddBeneficiary(false);
      setNewBeneficiary({ bank_name: '', bank_code: '', account_number: '', account_name: '', label: '' });
      toast.success(`Saved ${saved.account_name}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setAddingBeneficiary(false); }
  };

  const handleDeleteBeneficiary = async (id) => {
    try {
      await superAdminAPI.deleteBeneficiary(id);
      setBeneficiaries(prev => prev.filter(b => b.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed to remove'); }
  };

  const resolveNewBenAccount = useCallback(async () => {
    if (newBeneficiary.account_number.length < 10 || !newBeneficiary.bank_code) return;
    setNewBenResolving(true);
    try {
      const res = await superAdminAPI.resolveTransferAccount({
        bank_code: newBeneficiary.bank_code, account_number: newBeneficiary.account_number,
      });
      setNewBeneficiary(f => ({ ...f, account_name: res.account_name }));
    } catch { toast.error('Could not resolve account'); }
    finally { setNewBenResolving(false); }
  }, [newBeneficiary.account_number, newBeneficiary.bank_code]);

  useEffect(() => {
    if (newBeneficiary.account_number.length === 10 && newBeneficiary.bank_code) resolveNewBenAccount();
  }, [newBeneficiary.account_number, newBeneficiary.bank_code, resolveNewBenAccount]);

  const filteredNewBenBanks = banks.filter(b =>
    (b.name || b.bankName || '').toLowerCase().includes(newBenBankSearch.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>;

  // ── PROCESSING ─────────────────────
  if (step === 'processing' && result) {
    return (
      <div className="max-w-md mx-auto flex flex-col items-center py-12 px-4" data-testid="transfer-processing">
        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
        <p className="text-lg font-bold text-gray-900 mb-1">Transfer Processing</p>
        <p className="text-sm text-gray-500 mb-2 text-center">
          Transfer of {fmt(result.amount)} to {result.recipient_name} at {form.bank_name} is being processed.
        </p>
        <p className="text-xs text-gray-400 mb-6 text-center">
          {pollCount < 12 ? "We'll notify you when it completes." : "Transfer is still processing. Monitor in Transaction Errors for updates."}
        </p>
        <Card className="w-full border-gray-200">
          <CardContent className="p-4 space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Reference</span><span className="font-mono text-gray-900">{result.reference}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="text-gray-900">{fmt(result.amount)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Fee</span><span className="text-gray-900">{fmt(result.fee)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Source Wallet Balance</span><span className="font-bold text-gray-900">{fmt(result.new_balance)}</span></div>
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span className="text-amber-600 font-medium flex items-center gap-1">
                <Clock className="w-3 h-3" /> Processing
              </span>
            </div>
          </CardContent>
        </Card>
        {pollCount >= 12 && (
          <Button className="mt-6 bg-gray-900 hover:bg-gray-800 text-white" onClick={() => navigate('/super-admin')} data-testid="done-btn">
            Done
          </Button>
        )}
      </div>
    );
  }

  // ── FAILED ─────────────────────────
  if (step === 'failed' && result) {
    return (
      <div className="max-w-md mx-auto flex flex-col items-center py-12 px-4" data-testid="transfer-failed">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <XCircle className="w-8 h-8 text-red-500" />
        </div>
        <p className="text-lg font-bold text-gray-900 mb-1">Transfer Failed</p>
        <p className="text-sm text-gray-500 mb-2 text-center">
          Funds have been returned to the source wallet.
        </p>
        {result.failure_reason && (
          <p className="text-xs text-red-500 bg-red-50 px-3 py-1 rounded-full mb-4 text-center">{result.failure_reason}</p>
        )}
        <Card className="w-full border-gray-200 mt-2">
          <CardContent className="p-4 space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Reference</span><span className="font-mono text-gray-900">{result.reference}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Updated Balance</span><span className="font-bold text-gray-900">{fmt(result.new_balance)}</span></div>
          </CardContent>
        </Card>
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={resetForm} data-testid="try-again-btn">Try Again</Button>
          <Button className="bg-gray-900 hover:bg-gray-800 text-white" onClick={() => navigate('/super-admin')}>Done</Button>
        </div>
      </div>
    );
  }

  // ── SUCCESS ──────────────────────────
  if (step === 'success' && result) {
    return (
      <div className="max-w-md mx-auto flex flex-col items-center py-12 px-4" data-testid="transfer-success">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(result.amount)}</p>
        <p className="text-sm text-gray-500 mb-1">sent to {result.recipient_name}</p>
        <Card className="w-full border-gray-200 mt-4">
          <CardContent className="p-4 space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Reference</span><span className="font-mono text-gray-900">{result.reference}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Fee</span><span className="text-gray-900">{fmt(result.fee)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Source Wallet</span><span className="text-gray-900">{selectedWallet?.label}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">New Balance</span><span className="font-bold text-gray-900">{fmt(result.new_balance)}</span></div>
          </CardContent>
        </Card>
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={resetForm} data-testid="send-again-btn">Send Again</Button>
          <Button className="bg-pink-500 hover:bg-pink-600 text-white" onClick={() => navigate('/super-admin')} data-testid="done-btn">Done</Button>
        </div>
      </div>
    );
  }

  // ── CONFIRM + OTP ────────────────────
  if (step === 'confirm') {
    return (
      <div className="max-w-md mx-auto space-y-5" data-testid="transfer-confirm">
        <button onClick={() => { setStep('form'); setOtp(''); setOtpSent(false); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-lg font-bold text-gray-900">Confirm Transfer</h2>
        <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
          <p className="text-xs text-amber-700">This will transfer real funds via Assets MFB. This action cannot be undone.</p>
        </div>
        <Card className="border-gray-200">
          <CardContent className="p-4 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">From</span><span className="font-medium text-gray-900 text-right text-xs">{selectedWallet?.label}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Balance</span><span className="text-gray-900">{fmt(selectedWallet?.balance)}</span></div>
            <div className="border-t my-2" />
            <div className="flex justify-between"><span className="text-gray-500">To</span><span className="font-medium text-gray-900">{form.account_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Bank</span><span className="text-gray-900">{form.bank_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Account</span><span className="font-mono text-gray-900">{form.account_number}</span></div>
            <div className="border-t my-2" />
            <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-bold text-gray-900">{fmt(amount)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Fee</span><span className="text-gray-900">{fmt(fee)}</span></div>
            <div className="flex justify-between border-t pt-2"><span className="font-semibold text-gray-900">Total</span><span className="font-bold text-gray-900">{fmt(total)}</span></div>
            {form.narration && <div className="flex justify-between"><span className="text-gray-500">Narration</span><span className="text-gray-900">{form.narration}</span></div>}
          </CardContent>
        </Card>

        <div>
          {!otpSent ? (
            <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white h-10 text-xs" disabled={sendingOtp} onClick={handleRequestOtp} data-testid="request-otp-btn">
              {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Send verification code to my email
            </Button>
          ) : (
            <>
              <Label className="text-sm font-medium text-gray-700">Enter 6-digit code sent to your email</Label>
              <div className="flex gap-2 mt-2 justify-center">
                {[0,1,2,3,4,5].map(i => (
                  <input key={i} type="text" inputMode="numeric" maxLength={1}
                    className="w-10 h-12 text-center text-lg font-bold border border-gray-300 rounded-lg focus:border-pink-500 focus:ring-1 focus:ring-pink-500 outline-none"
                    value={otp[i] || ''} data-testid={`otp-digit-${i}`}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, '');
                      const newOtp = otp.slice(0, i) + v + otp.slice(i + 1);
                      setOtp(newOtp);
                      if (v && i < 5) e.target.nextSibling?.focus();
                    }}
                  />
                ))}
              </div>
              <button onClick={handleRequestOtp} disabled={sendingOtp} className="text-xs text-pink-500 hover:text-pink-600 mt-2 block mx-auto">
                {sendingOtp ? 'Sending...' : 'Resend code'}
              </button>
            </>
          )}
        </div>
        {otpSent && (
          <Button className="w-full bg-pink-500 hover:bg-pink-600 text-white h-11" disabled={otp.length < 6 || sending} onClick={handleSend} data-testid="confirm-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Confirm & Send {fmt(amount)}
          </Button>
        )}
      </div>
    );
  }

  // ── TRANSFER FORM ────────────────────
  if (step === 'form' && selectedWallet) {
    return (
      <div className="max-w-md mx-auto space-y-5" data-testid="transfer-form">
        <button onClick={() => { setStep('wallet'); setSelectedWallet(null); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Change wallet
        </button>

        {/* Source wallet info */}
        <Card className="border-pink-200 bg-pink-50/30">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-pink-500" />
              <div>
                <p className="text-xs font-medium text-gray-900">{selectedWallet.label}</p>
                <p className="text-[10px] text-gray-500">{selectedWallet.virtual_account_number || 'No VA'}</p>
              </div>
            </div>
            <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(selectedWallet.balance)}</p>
          </CardContent>
        </Card>

        {/* Saved Beneficiaries */}
        {beneficiaries.length > 0 && (
          <div>
            <Label className="text-xs font-medium text-gray-600 mb-2 block">Saved Accounts</Label>
            <div className="space-y-1.5 max-h-36 overflow-y-auto">
              {beneficiaries.map(b => (
                <button key={b.id} onClick={() => handleUseBeneficiary(b)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 hover:border-pink-300 hover:bg-pink-50 transition-colors"
                  data-testid={`beneficiary-${b.id}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-600">
                      {b.account_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="text-left">
                      <p className="text-xs font-medium text-gray-900">{b.label || b.account_name}</p>
                      <p className="text-[10px] text-gray-400">{b.bank_name} &middot; ****{b.account_number.slice(-4)}</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-pink-500 font-medium">Use</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bank Selection */}
        <div className="space-y-3">
          <div className="relative">
            <Label className="text-xs font-medium text-gray-600">Destination Bank</Label>
            <button onClick={() => setShowBankList(!showBankList)} data-testid="select-bank-btn"
              className="w-full mt-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm hover:border-gray-400">
              <span className={form.bank_name ? 'text-gray-900' : 'text-gray-400'}>{form.bank_name || 'Select bank'}</span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            {showBankList && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 sticky top-0 bg-white border-b">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input placeholder="Search banks..." value={bankSearch} onChange={e => setBankSearch(e.target.value)}
                      className="h-8 pl-8 text-xs" data-testid="bank-search-input" />
                  </div>
                </div>
                {filteredBanks.map(b => (
                  <button key={b.code || b.bankCode} onClick={() => selectBank(b)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 text-gray-700"
                    data-testid={`bank-option-${b.code || b.bankCode}`}>
                    {b.name || b.bankName}
                  </button>
                ))}
                {filteredBanks.length === 0 && <p className="text-xs text-gray-400 p-3 text-center">No banks found</p>}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-medium text-gray-600">Account Number</Label>
            <Input value={form.account_number}
              onChange={e => setForm(f => ({ ...f, account_number: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
              placeholder="0123456789" className="h-9 mt-1" maxLength={10} data-testid="account-number-input" />
          </div>
          {resolving && <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Verifying account...</p>}
          {form.account_name && (
            <p className="text-xs text-emerald-600 font-medium flex items-center gap-1" data-testid="resolved-name">
              <CheckCircle className="w-3 h-3" /> {form.account_name}
            </p>
          )}
        </div>

        {/* Amount */}
        <div>
          <Label className="text-xs font-medium text-gray-600">Amount</Label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">NGN</span>
            <Input type="text" inputMode="numeric" value={form.amount} data-testid="amount-input"
              onChange={e => setForm(f => ({ ...f, amount: e.target.value.replace(/[^0-9]/g, '') }))}
              placeholder="0" className="h-10 pl-12 text-lg font-bold" style={{ fontFamily: 'JetBrains Mono' }} />
          </div>
          {amount > 0 && <p className="text-[10px] text-gray-400 mt-1">Transfer fee: {fmt(fee)} &middot; Total: {fmt(total)}</p>}
          {total > (selectedWallet?.balance || 0) && amount > 0 && (
            <p className="text-[10px] text-red-500 mt-1">Insufficient balance</p>
          )}
        </div>

        {/* Narration */}
        <div>
          <Label className="text-xs font-medium text-gray-600">Narration (optional)</Label>
          <Input value={form.narration} onChange={e => setForm(f => ({ ...f, narration: e.target.value }))}
            placeholder="e.g. Monthly allowance" className="h-9 mt-1" data-testid="narration-input" />
        </div>

        <Button className="w-full bg-pink-500 hover:bg-pink-600 text-white h-11" data-testid="proceed-btn"
          disabled={!form.account_name || !amount || total > (selectedWallet?.balance || 0)}
          onClick={() => setStep('confirm')}>
          <Send className="w-4 h-4 mr-2" /> Proceed {amount > 0 ? fmt(amount) : ''}
        </Button>
      </div>
    );
  }

  // ── WALLET SELECTION ─────────────────
  return (
    <div className="space-y-5" data-testid="transfer-wallet-select">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Transfer to Bank</h1>
        <p className="text-sm text-gray-500 mt-0.5">Send money from any wallet to an external bank account</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={walletSearch} onChange={e => setWalletSearch(e.target.value)}
            placeholder="Search wallets..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
            data-testid="wallet-search-input" />
        </div>
        <div className="flex gap-1.5">
          {[
            { key: '', label: 'All' },
            { key: 'student', label: 'Student' },
            { key: 'company', label: 'Company' },
          ].map(f => (
            <button key={f.key} onClick={() => setWalletFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${walletFilter === f.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`wallet-filter-${f.key || 'all'}`}>{f.label}</button>
          ))}
        </div>
      </div>

      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {wallets.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No wallets found</p>
        )}
        {wallets.map(w => (
          <Card key={w.id} className="border-gray-200 hover:border-pink-200 hover:shadow-sm transition cursor-pointer group"
            onClick={() => { setSelectedWallet(w); setStep('form'); }} data-testid={`wallet-item-${w.id}`}>
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${w.owner_type === 'company' ? 'bg-blue-50' : 'bg-pink-50'}`}>
                  {w.owner_type === 'company' ? <Building2 className="w-4 h-4 text-blue-500" /> : <User className="w-4 h-4 text-pink-500" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{w.label}</p>
                  <p className="text-[10px] text-gray-400">
                    {w.virtual_account_number || 'No VA'} &middot; {w.status}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(w.balance)}</p>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-pink-400 transition" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Saved Accounts Section */}
      <div className="border-t pt-5 mt-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookmarkCheck className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">Saved External Accounts</h2>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowAddBeneficiary(true)} data-testid="add-beneficiary-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Account
          </Button>
        </div>

        {beneficiaries.length === 0 && !showAddBeneficiary && (
          <p className="text-xs text-gray-400 text-center py-4">No saved external accounts. Add one to quickly transfer later.</p>
        )}

        {beneficiaries.map(b => (
          <div key={b.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200 mb-1.5" data-testid={`saved-account-${b.id}`}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-[10px] font-bold text-emerald-600">
                {b.account_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-900">{b.label || b.account_name}</p>
                <p className="text-[10px] text-gray-400">{b.bank_name} &middot; {b.account_number}</p>
              </div>
            </div>
            <button onClick={() => handleDeleteBeneficiary(b.id)} className="text-gray-300 hover:text-red-500 transition" data-testid={`delete-beneficiary-${b.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {/* Add Beneficiary Form */}
        {showAddBeneficiary && (
          <Card className="border-emerald-200 bg-emerald-50/30 mt-2" data-testid="add-beneficiary-form">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-emerald-700">Add External Account</p>
                <button onClick={() => { setShowAddBeneficiary(false); setNewBeneficiary({ bank_name: '', bank_code: '', account_number: '', account_name: '', label: '' }); }}>
                  <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </button>
              </div>

              {/* Bank */}
              <div className="relative">
                <Label className="text-xs font-medium text-gray-600">Bank</Label>
                <button onClick={() => setShowNewBenBankList(!showNewBenBankList)}
                  className="w-full mt-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm hover:border-gray-400 bg-white"
                  data-testid="new-ben-select-bank">
                  <span className={newBeneficiary.bank_name ? 'text-gray-900 text-xs' : 'text-gray-400 text-xs'}>{newBeneficiary.bank_name || 'Select bank'}</span>
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </button>
                {showNewBenBankList && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="p-2 sticky top-0 bg-white border-b">
                      <Input placeholder="Search..." value={newBenBankSearch} onChange={e => setNewBenBankSearch(e.target.value)} className="h-7 text-xs" />
                    </div>
                    {filteredNewBenBanks.map(bk => (
                      <button key={bk.code || bk.bankCode} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700"
                        onClick={() => {
                          setNewBeneficiary(f => ({ ...f, bank_name: bk.name || bk.bankName, bank_code: bk.code || bk.bankCode, account_name: '' }));
                          setShowNewBenBankList(false); setNewBenBankSearch('');
                        }}>
                        {bk.name || bk.bankName}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Account Number */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Account Number</Label>
                <Input value={newBeneficiary.account_number}
                  onChange={e => setNewBeneficiary(f => ({ ...f, account_number: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  placeholder="0123456789" className="h-8 mt-1 text-xs" maxLength={10} data-testid="new-ben-account-number" />
              </div>
              {newBenResolving && <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Verifying...</p>}
              {newBeneficiary.account_name && (
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1" data-testid="new-ben-resolved-name">
                  <CheckCircle className="w-3 h-3" /> {newBeneficiary.account_name}
                </p>
              )}

              {/* Label */}
              <div>
                <Label className="text-xs font-medium text-gray-600">Label (optional)</Label>
                <Input value={newBeneficiary.label}
                  onChange={e => setNewBeneficiary(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Supplier - Okafor" className="h-8 mt-1 text-xs" data-testid="new-ben-label" />
              </div>

              <Button size="sm" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={!newBeneficiary.account_name || addingBeneficiary}
                onClick={handleAddBeneficiary} data-testid="save-beneficiary-btn">
                {addingBeneficiary ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                Save Account
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
